import { createClient } from '@supabase/supabase-js'

// ============================================================================
// INTERNATIONALIZATION (i18n)
// ============================================================================

// Detect browser language (de or en, fallback to en)
const browserLang = navigator.language || navigator.userLanguage
const lang = browserLang.startsWith('de') ? 'de' : 'en'

console.log('[Auth Landing] Browser language:', browserLang, '→ Using:', lang)

// Translations
const translations = {
  de: {
    loading: {
      title: 'Anmeldung wird abgeschlossen …',
      message: 'Bitte einen Moment Geduld – wir verbinden dich sicher mit HappyTask.'
    },
    redirecting: {
      title: 'Weiterleitung zu {provider}...',
      message: 'Bitte schließe den Anmeldevorgang ab.'
    },
    success: {
      title: 'Erfolgreich!',
      message: 'Weiterleitung zu HappyTask...'
    },
    error: {
      title: 'Authentifizierung fehlgeschlagen',
      fallback: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut.'
    },
    electronFallback: {
      title: 'Authentifizierung erfolgreich',
      message: 'Alles erledigt! Du kannst die Seite nun schließen.'
    },
    errors: {
      missingConfig: 'Supabase-Konfiguration fehlt. Bitte setze VITE_SUPABASE_URL und VITE_SUPABASE_ANON_KEY.'
    }
  },
  en: {
    loading: {
      title: 'Completing sign-in…',
      message: 'Please wait a moment – we\'re securely connecting you to HappyTask.'
    },
    redirecting: {
      title: 'Redirecting to {provider}...',
      message: 'Please complete the sign-in process.'
    },
    success: {
      title: 'Success!',
      message: 'Redirecting to HappyTask...'
    },
    error: {
      title: 'Authentication Failed',
      fallback: 'An unexpected error occurred. Please try again.'
    },
    electronFallback: {
      title: 'Authentication successful',
      message: 'All done! You can now close this page.'
    },
    errors: {
      missingConfig: 'Supabase configuration missing. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
    }
  }
}

// Translation helper function
const t = (key) => {
  const keys = key.split('.')
  let value = translations[lang]

  for (const k of keys) {
    value = value?.[k]
  }

  // Fallback to English if translation missing
  if (!value) {
    value = translations.en
    for (const k of keys) {
      value = value?.[k]
    }
  }

  return value || key
}

// ============================================================================
// PROVIDER
// ============================================================================

// The `provider` query parameter is passed straight to Supabase, so it is
// checked against this list. A missing parameter still means Google — older
// app versions don't send one.
// A Map, not an object literal: `PROVIDER_LABELS['constructor']` on a plain
// object is truthy and would slip through the check below.
const PROVIDER_LABELS = new Map([
  ['google', 'Google'],
  ['apple', 'Apple']
])

// ============================================================================
// UI ELEMENTS
// ============================================================================

const titleEl = document.getElementById('title')
const messageEl = document.getElementById('message')
const spinnerEl = document.getElementById('spinner')
const errorEl = document.getElementById('error')
const errorMessageEl = document.getElementById('error-message')

/**
 * Show error message in UI
 */
function showError(message) {
  titleEl.textContent = t('error.title')
  messageEl.style.display = 'none'
  spinnerEl.style.display = 'none'
  errorEl.style.display = 'block'
  errorMessageEl.textContent = message
}

/**
 * Update status message
 */
function updateStatus(title, message) {
  titleEl.textContent = title
  messageEl.textContent = message
}

/**
 * Main OAuth Handler
 */
async function handleOAuth() {
  try {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    const platform = urlParams.get('platform') || 'web'
    const provider = urlParams.get('provider') || 'google'

    if (!PROVIDER_LABELS.has(provider)) {
      throw new Error(`Unknown provider: ${provider}`)
    }

    console.log('[Auth Landing] Platform:', platform)
    console.log('[Auth Landing] Provider:', provider)
    console.log('[Auth Landing] Current URL:', window.location.href)

    // Check if this is an OAuth callback (has access_token in hash)
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const accessToken = hashParams.get('access_token')
    const refreshToken = hashParams.get('refresh_token')
    const error = hashParams.get('error')
    const errorDescription = hashParams.get('error_description')

    // Handle OAuth errors
    if (error) {
      console.error('[Auth Landing] OAuth error:', error, errorDescription)
      showError(errorDescription || error)
      return
    }

    // If we have tokens, redirect back to the app
    if (accessToken && refreshToken) {
      console.log('[Auth Landing] Tokens received, redirecting to app...')
      updateStatus(t('success.title'), t('success.message'))

      // Build redirect URL based on platform
      let redirectUrl
      if (platform === 'electron') {
        // Electron: Use deep link
        redirectUrl = `happytask://auth/callback#access_token=${accessToken}&refresh_token=${refreshToken}`
        console.log('[Auth Landing] Redirecting to Electron via deep link:', redirectUrl)

        // Try to redirect, but also show manual fallback for development
        try {
          window.location.href = redirectUrl
        } catch (err) {
          console.error('[Auth Landing] Deep link failed:', err)
        }

        // Show fallback message after 2 seconds if deep link doesn't work
        setTimeout(() => {
          titleEl.textContent = t('electronFallback.title')
          messageEl.textContent = t('electronFallback.message')
          spinnerEl.style.display = 'none'
        }, 2000)
      } else {
        // Web: Redirect to web app with query parameters (NOT hash - BrowserRouter doesn't support hash)
        redirectUrl = `https://app.happytask.app/auth/callback?access_token=${accessToken}&refresh_token=${refreshToken}`
        console.log('[Auth Landing] Redirecting to HappyTask app:', redirectUrl)

        setTimeout(() => {
          window.location.href = redirectUrl
        }, 1000)
      }
      return
    }

    // If no tokens yet, initiate OAuth flow
    console.log('[Auth Landing] Initiating OAuth flow...')
    updateStatus(t('redirecting.title').replace('{provider}', PROVIDER_LABELS.get(provider)), t('redirecting.message'))

    // Initialize Supabase client
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error(t('errors.missingConfig'))
    }

    const supabase = createClient(supabaseUrl, supabaseAnonKey)

    // Build redirect URL for OAuth callback
    const redirectTo = `${window.location.origin}${window.location.pathname}?platform=${platform}&provider=${provider}`
    console.log('[Auth Landing] OAuth redirect URL:', redirectTo)

    // Initiate OAuth flow
    const { data, error: signInError } = await supabase.auth.signInWithOAuth({
      provider: provider,
      options: {
        redirectTo: redirectTo,
      },
    })

    if (signInError) {
      throw signInError
    }

    console.log('[Auth Landing] OAuth initiated successfully')

  } catch (err) {
    console.error('[Auth Landing] Error:', err)
    showError(err.message || t('error.fallback'))
  }
}

// Run on page load
handleOAuth() // Temporarily disabled for design/styling adjustments
